import { useCallback, useEffect, useRef, useState } from "react";
import { companiesService } from "../services/companies.service";

const COMPANY_PAGE_SIZE = 20;

const sameAccount = (one, other) => (one ?? null) === (other ?? null);

export function useCompanyOptions({ accountId = null } = {}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef("");
  const offsetRef = useRef(0);
  const totalRef = useRef(0);
  const requestIdRef = useRef(0);
  const pinnedRef = useRef(null);

  const withPinned = useCallback(
    (items) => {
      const pinned = pinnedRef.current;
      if (!pinned) return items;
      if (!sameAccount(pinned.accountId, accountId)) return items;
      return [
        pinned.company,
        ...items.filter((item) => Number(item.id) !== Number(pinned.company.id)),
      ];
    },
    [accountId],
  );

  const fetchCompanies = useCallback(
    async ({ search, reset }) => {
      const nextSearch = (search ?? "").trim();
      const offset = reset ? 0 : offsetRef.current;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      searchRef.current = nextSearch;
      if (reset) {
        offsetRef.current = 0;
        totalRef.current = 0;
      }
      setLoading(true);
      try {
        const { results, count } = await companiesService.list({
          ...(nextSearch ? { search: nextSearch } : {}),
          ...(accountId != null ? { account_id: accountId } : {}),
          limit: COMPANY_PAGE_SIZE,
          offset,
        });
        if (requestIdRef.current !== requestId) return;
        offsetRef.current = offset + (results?.length ?? 0);
        totalRef.current = count ?? 0;
        setOptions((previous) =>
          withPinned(reset ? results ?? [] : [...previous, ...(results ?? [])]),
        );
      } catch {
        if (reset && requestIdRef.current === requestId) {
          offsetRef.current = 0;
          totalRef.current = 0;
          setOptions(withPinned([]));
        }
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [accountId, withPinned],
  );

  const search = useCallback(
    (query) => fetchCompanies({ search: query, reset: true }),
    [fetchCompanies],
  );

  const loadMore = useCallback(() => {
    if (loading || options.length >= totalRef.current) return;
    fetchCompanies({ search: searchRef.current, reset: false });
  }, [fetchCompanies, loading, options.length]);

  useEffect(() => {
    pinnedRef.current = null;
    offsetRef.current = 0;
    totalRef.current = 0;
    requestIdRef.current += 1;
    setOptions([]);
  }, [accountId]);

  const pin = useCallback(
    (company) => {
      if (!company?.id) return;
      pinnedRef.current = { accountId, company };
      setOptions((previous) => withPinned(previous));
    },
    [accountId, withPinned],
  );

  return { options, loading, search, loadMore, pin };
}
